/**
 * @file adminApi.js
 * @description 관리자 콘솔이 사용하는 서버 API 호출을 모아둔 클라이언트입니다.
 * @used-in hooks/useAdminSession.js, pages/admin/*
 *
 * [기술] 인증은 서버가 발급한 httpOnly 세션 쿠키로 이뤄집니다.
 *        쿠키는 자바스크립트에서 읽을 수 없으므로 요청에 토큰을 실어 보내는 코드가 없습니다.
 *        개발(CRA 프록시)과 운영(Express가 build 서빙) 모두 동일 출처라
 *        credentials 기본값(same-origin)만으로 쿠키가 전송됩니다.
 */

// [비즈니스] 세션 만료를 다른 오류와 구분해, 화면이 곧바로 로그인 상태로 되돌아갈 수 있게 합니다.
export class AdminAuthError extends Error {
  constructor(message = '로그인이 필요합니다.') {
    super(message);
    this.name = 'AdminAuthError';
  }
}

// [기능] 응답에서 서버가 보낸 오류 메시지를 최대한 살려 읽어냅니다.
//        JSON이 아니면 상태 코드 기반의 기본 문구를 반환합니다.
async function readErrorMessage(response, fallback) {
  try {
    const data = await response.json();
    if (data?.message) return data.message;
  } catch {
    // [기술] 프록시 오류 등으로 HTML이 올 수 있으므로 파싱 실패는 무시하고 기본 문구를 씁니다.
  }
  return `${fallback} (${response.status})`;
}

// [기능] JSON API를 호출하고 파싱된 본문을 반환합니다.
//        401이면 AdminAuthError를, 그 외 실패는 일반 Error를 던집니다.
async function requestJson(url, { method = 'GET', body, fallbackMessage = '요청에 실패했습니다.' } = {}) {
  let response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Error('네트워크 오류가 발생했습니다. 서버가 실행 중인지 확인해 주세요.');
  }

  if (response.status === 401) {
    throw new AdminAuthError(await readErrorMessage(response, '로그인이 필요합니다.'));
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, fallbackMessage));
  }

  // [기술] 204 No Content는 본문이 없어 json() 파싱이 실패하므로 분기합니다.
  if (response.status === 204) return null;
  return response.json();
}

// ───── 인증 ─────

// [기능] 관리자 키로 로그인해 세션 쿠키를 발급받습니다.
export function login(key) {
  return requestJson('/api/admin/login', {
    method: 'POST',
    body: { key },
    fallbackMessage: '로그인에 실패했습니다.',
  });
}

// [기능] 세션 쿠키를 삭제해 로그아웃합니다.
export function logout() {
  return requestJson('/api/admin/logout', { method: 'POST', fallbackMessage: '로그아웃에 실패했습니다.' });
}

// [기능] 현재 로그인 여부를 확인합니다. 로그인 상태면 true를 반환합니다.
export async function fetchSession() {
  try {
    await requestJson('/api/admin/session');
    return true;
  } catch (error) {
    if (error instanceof AdminAuthError) return false;
    throw error;
  }
}

// ───── 문구 오버라이드 ─────

// [기능] 문구 오버라이드 전체를 조회합니다.
// [비즈니스] GET은 인증 없이 공개 페이지에서 사용하며 주입이 없는 환경에서만 호출합니다.
// [기술] 공용 요청 함수를 재사용해 기존 인증 오류와 네트워크 오류 처리를 유지합니다.
export function fetchContent() {
  return requestJson('/api/content', { fallbackMessage: '문구를 불러오지 못했습니다.' });
}

// [기능] 문구 오버라이드 전체를 교체 저장하고 저장된 레코드를 반환합니다.
// [비즈니스] 전체 교체이므로 호출 측이 최신 서버 맵과 병합한 완전한 맵을 넘겨야 합니다.
// [기술] 서버가 req.body.values를 읽으므로 반드시 { values }로 감쌉니다.
export function saveContent(values) {
  return requestJson('/api/content', {
    method: 'PUT',
    body: { values },
    fallbackMessage: '문구 저장에 실패했습니다.',
  });
}

// ───── 연혁 ─────

// [기능] 연혁 조회(GET)는 인증이 없어 공개 페이지에서도 사용합니다.
export function fetchHistory() {
  return requestJson('/api/history', { fallbackMessage: '연혁을 불러오지 못했습니다.' });
}

export function saveHistory(items) {
  return requestJson('/api/history', { method: 'PUT', body: items, fallbackMessage: '연혁 저장에 실패했습니다.' });
}

// ───── FAQ ─────

export function fetchFaq() {
  return requestJson('/api/faq', { fallbackMessage: 'FAQ를 불러오지 못했습니다.' });
}

export function saveFaq(items) {
  return requestJson('/api/faq', { method: 'PUT', body: items, fallbackMessage: 'FAQ 저장에 실패했습니다.' });
}

// ───── 뉴스 ─────

export function fetchNews() {
  return requestJson('/api/news', { fallbackMessage: '뉴스를 불러오지 못했습니다.' });
}

export function createNews(payload) {
  return requestJson('/api/news', { method: 'POST', body: payload, fallbackMessage: '뉴스 등록에 실패했습니다.' });
}

export function updateNews(slug, payload) {
  return requestJson(`/api/news/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: payload,
    fallbackMessage: '뉴스 수정에 실패했습니다.',
  });
}

export function deleteNews(slug) {
  return requestJson(`/api/news/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    fallbackMessage: '뉴스 삭제에 실패했습니다.',
  });
}

// ───── 이미지 업로드 ─────

// [기능] 이미지 파일을 업로드하고 본문에 삽입할 URL을 반환합니다.
// [기술] FormData를 보낼 때 Content-Type을 직접 지정하면 multipart 경계 문자열(boundary)이
//        빠져 서버가 본문을 파싱하지 못합니다. 브라우저가 자동으로 붙이도록 헤더를 비워 둡니다.
export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('image', file);

  let response;
  try {
    response = await fetch('/api/upload', { method: 'POST', credentials: 'same-origin', body: formData });
  } catch {
    throw new Error('네트워크 오류로 이미지를 업로드하지 못했습니다.');
  }

  if (response.status === 401) {
    throw new AdminAuthError();
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, '이미지 업로드에 실패했습니다.'));
  }

  const data = await response.json();
  return data.url;
}

// ───── 초기화(seed) ─────

// [기능] 초기화 확인 창에 보여 줄 현재·초기 데이터 건수를 조회합니다.
export function fetchResetPreview(target) {
  return requestJson(`/api/admin/reset/${encodeURIComponent(target)}`, {
    fallbackMessage: '초기화 정보를 불러오지 못했습니다.',
  });
}

// [기능] 현재 파일을 백업한 뒤 초기 데이터로 바꾼 결과를 반환합니다.
export function resetToSeed(target) {
  return requestJson(`/api/admin/reset/${encodeURIComponent(target)}`, {
    method: 'POST',
    fallbackMessage: '초기화에 실패했습니다.',
  });
}
